import axios from "axios";
import { WEBSERVICE } from "../../CoreDataService";

/**
 * Base API URL resolver
 */
const getBaseUrl = () => {
  const raw =
    process.env.REACT_APP_WEBSERVICE ||
    WEBSERVICE ||
    "https://accounts.paybito.com/api/";
  return raw.replace(/\/+$/, "");
};

/**
 * Common headers including Bearer token if logged in
 */
const getHeaders = () => {
  const token = localStorage.getItem("access_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `BEARER ${token}` } : {}),
  };
};

/**
 * Get current user UUID
 */
export const getUserUuid = () => {
  return (
    localStorage.getItem("uuid") ||
    localStorage.getItem("userUuid") ||
    ""
  );
};

// ── Layout Setting IDs Mapping ──────────────────────────────────────────────
export const LAYOUT_SETTING_MAP = {
  1: "DEFAULT",
  2: "BINANCE_PRO",
  3: "CHART_RIGHT_STACKED_BOTTOM",
  4: "COINBASE_ADVANCED",
  5: "FOUR_COLUMN_PANORAMIC",
  6: "CHART_LEFT_INVERTED",
  7: "SWAPPED_BOTTOM_CENTER",
};

export const PRESET_TO_SETTING_ID = {
  DEFAULT: 1,
  BINANCE_PRO: 2,
  CHART_RIGHT_STACKED_BOTTOM: 3,
  COINBASE_ADVANCED: 4,
  FOUR_COLUMN_PANORAMIC: 5,
  CHART_LEFT_INVERTED: 6,
  SWAPPED_BOTTOM_CENTER: 7,
};

// ── Color Setting IDs Mapping (supports settingId 21-23 and 1-3) ────────────
export const COLOR_SETTING_MAP = {
  1: "TRADITIONAL",
  2: "FRESH",
  3: "COLOR_VISION_DEFICIENCY",
  21: "TRADITIONAL",
  22: "FRESH",
  23: "COLOR_VISION_DEFICIENCY",
};

export const COLOR_TO_SETTING_ID = {
  TRADITIONAL: 21,
  FRESH: 22,
  COLOR_VISION_DEFICIENCY: 23,
};

/**
 * Match a color setting object from API to our internal palette ID
 * Checks settingId first, then name
 */
export const matchColorPreference = (setting) => {
  if (!setting) return null;
  if (COLOR_SETTING_MAP[setting.settingId]) {
    return COLOR_SETTING_MAP[setting.settingId];
  }
  const name = (setting.name || "").toLowerCase();
  if (name.includes("emerald") || name.includes("traditional")) return "TRADITIONAL";
  if (name.includes("neon") || name.includes("fresh")) return "FRESH";
  if (name.includes("electric") || name.includes("tangerine") || name.includes("deficiency")) return "COLOR_VISION_DEFICIENCY";
  return null;
};

/**
 * Match a layout setting object from API to our internal preset ID
 * Checks settingId first, then name
 */
export const matchLayoutPreset = (setting) => {
  if (!setting) return null;
  if (LAYOUT_SETTING_MAP[setting.settingId]) {
    return LAYOUT_SETTING_MAP[setting.settingId];
  }
  const name = (setting.name || "").toLowerCase();
  if (name.includes("classic")) return "DEFAULT";
  if (name.includes("advanced") || name.includes("binance")) return "BINANCE_PRO";
  if (name.includes("right")) return "CHART_RIGHT_STACKED_BOTTOM";
  if (name.includes("coinbase") || name.includes("pro trader") || name.includes("pro-trader")) return "COINBASE_ADVANCED";
  if (name.includes("4-column") || name.includes("four")) return "FOUR_COLUMN_PANORAMIC";
  if (name.includes("left")) return "CHART_LEFT_INVERTED";
  if (name.includes("swapped") || name.includes("swap")) return "SWAPPED_BOTTOM_CENTER";
  return null;
};

/**
 * Fetch available broker exchange settings for user by type ('Layout' or 'Color')
 *
 * Endpoint: POST /user/getExchangeSettings
 * Payload: { "userUuid": "", "type": "Layout" | "Color" }
 */
export const getAvailableExchangeSettings = async (type = "Layout") => {
  try {
    const userUuid = getUserUuid();
    const payload = {
      userUuid: userUuid || "",
      type,
    };

    const res = await axios.post(
      `${getBaseUrl()}/user/getExchangeSettings`,
      payload,
      { headers: getHeaders() }
    );

    const data = res?.data;
    if (data?.errorCode === 0 && Array.isArray(data?.exchangeSettings)) {
      return data.exchangeSettings;
    }
    if (Array.isArray(data?.exchangeSettings)) {
      return data.exchangeSettings;
    }
    return [];
  } catch (err) {
    console.error(`[exchangeSettingsService] Error fetching ${type} settings:`, err);
    return [];
  }
};

/**
 * Update user's default layout or color setting
 *
 * Endpoint: POST /user/updateExchangeSettings
 * Payload: { "userUuid": "", "settingId": "2" }
 */
export const updateUserDefaultSetting = async (settingId) => {
  try {
    const userUuid = getUserUuid();
    const payload = {
      userUuid: userUuid || "",
      settingId: String(settingId),
    };

    const res = await axios.post(
      `${getBaseUrl()}/user/updateExchangeSettings`,
      payload,
      { headers: getHeaders() }
    );

    return res?.data;
  } catch (err) {
    console.error(`[exchangeSettingsService] Error updating default setting (${settingId}):`, err);
    throw err;
  }
};

/**
 * Sync active layout and color preferences with broker settings on page load
 */
export const syncExchangeSettingsOnLoad = async ({
  currentPresetId,
  onSetPreset,
  currentColorId,
  onSetColor,
}) => {
  try {
    const [layouts, colors] = await Promise.all([
      getAvailableExchangeSettings("Layout"),
      getAvailableExchangeSettings("Color"),
    ]);

    // 1. Sync Layout:
    // If an item is set as default in backend (isDefault === 1), use that default layout;
    // If nothing is set by default, open with Classic layout ("DEFAULT").
    if (Array.isArray(layouts) && layouts.length > 0) {
      const defaultItem = layouts.find(
        (item) => Number(item?.isDefault) === 1
      );

      if (defaultItem) {
        const targetPreset = matchLayoutPreset(defaultItem) || "DEFAULT";
        if (targetPreset && onSetPreset) {
          onSetPreset(targetPreset);
        }
      } else {
        // Nothing is set by default in backend -> open with Classic layout
        const targetPreset = "DEFAULT";
        if (targetPreset && onSetPreset) {
          onSetPreset(targetPreset);
        }
      }
    }

    // 2. Sync Color:
    // If an item is set as default in backend (isDefault === 1), use that default color;
    // Otherwise open with FRESH (neon).
    if (Array.isArray(colors) && colors.length > 0) {
      const defaultColor = colors.find(
        (item) => Number(item?.isDefault) === 1
      );

      if (defaultColor) {
        const targetColor = matchColorPreference(defaultColor) || "FRESH";
        if (targetColor && onSetColor) {
          onSetColor(targetColor);
        }
      } else {
        const targetColor = "FRESH";
        if (targetColor && onSetColor) {
          onSetColor(targetColor);
        }
      }
    }
  } catch (e) {
    console.error("[exchangeSettingsService] syncExchangeSettingsOnLoad error:", e);
  }
};
